/**
 * Metric & Dimension Registry (Story 5.35)
 *
 * Single source of truth for metric→platform mapping. Replaces the
 * dual hardcoded METRIC_PLATFORM_MAP in templateUtils.ts (frontend)
 * and metric-platform-map.ts (server).
 *
 * Iterates registered adapters via ADAPTERS from registry.ts and
 * builds the canonical metric→platform map dynamically.
 */
import type { Platform } from "@provenleap/db";
import type { MetricDefinition, DimensionDefinition } from "./adapter";
import { ADAPTERS } from "./registry";

// ============================================================================
// Types
// ============================================================================

/**
 * Registry entry for a single integration's metrics and dimensions.
 */
export interface IntegrationMetricRegistry {
  readonly platform: string;
  readonly metrics: MetricDefinition[];
  readonly dimensions: DimensionDefinition[];
  /**
   * Registry schema version (not the platform's API version).
   * Incremented when metric/dimension definitions change shape.
   * For platform API versions, see Integration Mapping Reference.
   */
  readonly apiVersion: string;
}

// ============================================================================
// Priority Order for Ambiguous Metric Keys
// ============================================================================

/**
 * Determines which platform "owns" a metric key when multiple adapters
 * share the same key (e.g., `clicks` in GSC and Google Ads).
 *
 * Order matches the previous hardcoded METRIC_PLATFORM_MAP behavior
 * for backward compatibility.
 */
const METRIC_PRIORITY: Platform[] = [
  "google-analytics-4",
  "google-search-console",
  "google-ads",
  "youtube-analytics",
  "google-business-profile",
  "google-pagespeed",
  // Social platforms (future epics)
  "facebook-pages",
  "instagram-business",
  "linkedin-pages",
  "facebook-ads",
  "instagram-ads",
  "linkedin-ads",
  "tiktok-business",
  // Advertising (future)
  "microsoft-ads",
  "pinterest-ads",
  "snapchat-ads",
  "reddit-ads",
  "amazon-ads",
  // SEO (future)
  "ahrefs",
  "semrush",
  "moz",
  // Email & Commerce (future)
  "mailchimp",
  "shopify",
  // Call Tracking (future)
  "callrail",
  "calltrackingmetrics",
  // Reviews (future)
  "google-reviews",
  "trustpilot",
  "yelp",
  // CRM (future)
  "hubspot",
];

// ============================================================================
// Cached Map (built once, reused)
// ============================================================================

let cachedMap: Record<string, string> | null = null;

/**
 * Reset the cached map. Used in tests to ensure fresh builds.
 * @internal
 */
export function _resetMetricPlatformMapCache(): void {
  cachedMap = null;
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Builds a metric key → platform ID map by iterating all registered adapters.
 * First adapter in METRIC_PRIORITY that registers a key "owns" it.
 * Result is cached for performance.
 *
 * @returns Record mapping metric keys to platform IDs (e.g., `{ sessions: "google-analytics-4" }`)
 */
export function buildMetricPlatformMap(): Record<string, string> {
  if (cachedMap) return cachedMap;

  const map: Record<string, string> = {};

  // Process adapters in priority order first
  for (const platform of METRIC_PRIORITY) {
    const adapter = ADAPTERS[platform];
    if (!adapter) continue;

    const metrics = adapter.getAvailableMetrics();
    for (const metric of metrics) {
      // First adapter to register a key wins (preserves backward compat)
      if (!(metric.key in map)) {
        map[metric.key] = platform;
      }
    }
  }

  // Also process any adapters not in priority list (future-proofing)
  for (const [platform, adapter] of Object.entries(ADAPTERS)) {
    if (!adapter || METRIC_PRIORITY.includes(platform as Platform)) continue;
    const metrics = adapter.getAvailableMetrics();
    for (const metric of metrics) {
      if (!(metric.key in map)) {
        map[metric.key] = platform;
      }
    }
  }

  cachedMap = map;
  return map;
}

/**
 * Returns the full metric/dimension registry for a specific platform.
 * Calls the adapter's getAvailableMetrics() and getAvailableDimensions().
 *
 * @returns Registry entry or undefined if adapter is not registered
 */
export function getRegistryForPlatform(
  platform: Platform,
): IntegrationMetricRegistry | undefined {
  const adapter = ADAPTERS[platform];
  if (!adapter) return undefined;

  return {
    platform,
    metrics: adapter.getAvailableMetrics(),
    dimensions: adapter.getAvailableDimensions(),
    apiVersion: "v1", // Incremented when metric definitions change
  };
}

/**
 * Returns all metrics from all registered adapters, flattened with platform source.
 * Useful for building cross-platform metric selectors.
 */
export function getAllRegisteredMetrics(): Array<
  MetricDefinition & { platform: string }
> {
  const result: Array<MetricDefinition & { platform: string }> = [];

  for (const [platform, adapter] of Object.entries(ADAPTERS)) {
    if (!adapter) continue;
    const metrics = adapter.getAvailableMetrics();
    for (const metric of metrics) {
      result.push({ ...metric, platform });
    }
  }

  return result;
}

/**
 * Returns all dimensions from all registered adapters, flattened with platform source.
 * Useful for building cross-platform dimension selectors.
 */
export function getAllRegisteredDimensions(): Array<
  DimensionDefinition & { platform: string }
> {
  const result: Array<DimensionDefinition & { platform: string }> = [];

  for (const [platform, adapter] of Object.entries(ADAPTERS)) {
    if (!adapter) continue;
    const dimensions = adapter.getAvailableDimensions();
    for (const dimension of dimensions) {
      result.push({ ...dimension, platform });
    }
  }

  return result;
}
